import { Button, Card, formatDate, Skeleton } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getCreditNote, getInvoice, type InvoiceDocument } from "../../api/invoices";
import { SegmentedControl } from "@helmr/ui";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { useAppContext, useMoney, useScope } from "./hooks";
import { PageHead, QueryFailure } from "./states";
import { billing as t } from "./strings";
import s from "./billing.module.css";

const addressText = (a: unknown): string =>
  typeof a === "string" ? a : a && typeof a === "object" ? Object.values(a as Record<string, unknown>).filter((v) => typeof v === "string" && v).join(", ") : "";

/** Read-only render of the stored document. Every figure comes from the document; nothing is computed here. */
export function InvoicePage({ creditNote = false }: { creditNote?: boolean }) {
  const { id = "" } = useParams();
  const ctx = useAppContext();
  const { client, scopeKey } = useScope();
  const [width, setWidth] = useState<"a4" | "receipt">("a4");
  const q = useQuery({ queryKey: [creditNote ? "credit-note" : "invoice", ...scopeKey, id], queryFn: () => (creditNote ? getCreditNote : getInvoice)(client, id), retry: false });
  if (ctx.status !== "loading" && !areaAllows("billing", ctx.has)) return <NoAccess />;
  return (
    <div className={s.page}>
      <title>Invoice · Helmr</title>
      <div className={s.noPrint}>
        <Button as={Link} to={creditNote ? "/billing?tab=creditNotes" : "/billing?tab=invoices"}>{t.bill.back}</Button>
        <SegmentedControl label="Paper" value={width} onChange={setWidth} options={[{ value: "a4", label: t.invoice.a4 }, { value: "receipt", label: t.invoice.receipt }]} />
        <Button variant="primary" onClick={() => window.print()} disabled={!q.data}>{t.invoice.print}</Button>
      </div>
      {q.isPending ? <Card><div className={s.skel} aria-busy="true" aria-label="Loading invoice"><Skeleton height={28} /><Skeleton height={160} /><Skeleton height={60} /></div></Card>
        : q.error ? <QueryFailure error={q.error} what={creditNote ? "this credit note" : "this invoice"} missing={creditNote ? "Credit note not found" : "Invoice not found"} onRetry={() => void q.refetch()} />
        : <Sheet doc={q.data} receipt={width === "receipt"} />}
    </div>
  );
}

function Sheet({ doc, receipt }: { doc: InvoiceDocument; receipt: boolean }) {
  const ctx = useAppContext();
  const { fmt } = useMoney(ctx);
  const igst = doc.totals.igst > 0;
  const heading = doc.docType === "credit_note" ? t.invoice.creditNote : doc.docType === "bill_of_supply" ? t.invoice.billOfSupply : t.invoice.taxInvoice;
  return (
    <article className={`${s.sheet} ${receipt ? s.receipt : ""}`} aria-label={`${heading} ${doc.number}`} data-paper={receipt ? "receipt" : "a4"}>
      <header>
        <h2>{heading} {doc.number}</h2>
        <div>{formatDate(doc.issueDate, ctx.locale, "full")}</div>
        {doc.invoiceNumber && doc.invoiceDate ? <div>{t.invoice.against(doc.invoiceNumber, formatDate(doc.invoiceDate, ctx.locale, "short"))}</div> : null}
      </header>
      <div className={s.twoCol}>
        <div>
          <strong>{t.invoice.seller}</strong>
          <div>{doc.seller.legalName}</div><div>{doc.seller.branchName}</div>
          <div>{addressText(doc.seller.address)}</div>
          {doc.seller.taxId ? <div>{t.invoice.gstin}: {doc.seller.taxId}</div> : null}
        </div>
        <div>
          <strong>{t.invoice.buyer}</strong>
          <div>{doc.buyer.legalName ?? doc.buyer.name}</div>
          {doc.buyer.taxId ? <div>{t.invoice.gstin}: {doc.buyer.taxId}</div> : null}
          {doc.placeOfSupply ? <div>{t.invoice.place}: {doc.placeOfSupply}</div> : null}
        </div>
      </div>
      <table>
        <thead><tr><th>Item</th><th>{t.invoice.hsn}</th><th className={s.r}>{t.invoice.qty}</th><th className={s.r}>{t.invoice.rate}</th><th className={s.r}>{t.invoice.taxable}</th><th className={s.r}>{igst ? t.invoice.igst : `${t.invoice.cgst} / ${t.invoice.sgst}`}</th><th className={s.r}>{t.invoice.total}</th></tr></thead>
        <tbody>
          {doc.lines.map((l) => (
            <tr key={l.billLineId}>
              <td>{l.description}</td><td>{l.hsnSac ?? ""}</td><td className={s.r}>{l.quantity}</td><td className={s.r}>{fmt(l.unitPrice)}</td><td className={s.r}>{fmt(l.taxableValue)}</td>
              <td className={s.r}>{l.igst > 0 ? fmt(l.igst) : `${fmt(l.cgst)} / ${fmt(l.sgst)}`}</td><td className={s.r}>{fmt(l.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {doc.taxSummary.length > 0 ? (
        <table aria-label={t.invoice.summary}>
          <thead><tr><th>{t.invoice.summary}</th><th className={s.r}>{t.invoice.taxable}</th><th className={s.r}>{t.invoice.cgst}</th><th className={s.r}>{t.invoice.sgst}</th><th className={s.r}>{t.invoice.igst}</th></tr></thead>
          <tbody>
            {doc.taxSummary.map((r) => <tr key={r.taxRateBp}><td>{r.taxRateBp / 100}%</td><td className={s.r}>{fmt(r.taxableValue)}</td><td className={s.r}>{fmt(r.cgst)}</td><td className={s.r}>{fmt(r.sgst)}</td><td className={s.r}>{fmt(r.igst)}</td></tr>)}
          </tbody>
        </table>
      ) : null}
      <footer>
        <div className={s.trow}><span>{t.invoice.taxable}</span><span className={s.num}>{fmt(doc.totals.taxable)}</span></div>
        {doc.totals.roundOff !== 0 ? <div className={s.trow}><span>{t.invoice.round}</span><span className={s.num}>{fmt(doc.totals.roundOff)}</span></div> : null}
        <div className={`${s.trow} ${s.grand}`}><span>{t.invoice.grand}</span><span className={s.num} data-testid="invoice-total">{fmt(doc.totals.total)}</span></div>
        {doc.notes ? <p>{doc.notes}</p> : null}
        {doc.reason ? <p>{doc.reason}</p> : null}
      </footer>
    </article>
  );
}
